// @ts-nocheck
import type { HeadstoneDTO } from "../api/client";
import { showView, getViewContainer, getCurrentView } from "./view-router";
import { openProfile } from "./my-headstones";
import {
  refreshPostsAccess,
  getHasHeadstones,
  getMyHeadstones,
} from "./posts-access";
import { navigateToHeadstone } from "../map/headstone-nav";
import { navigateToMemorial } from "./memorial-deep-link";
import { dtoToLegacy, type LegacyHeadstone } from "../utils/dto";
import { holeIdFromCoords } from "../utils/hole-id";
import { renderPetPhotoHtml } from "../utils/pet-photo-placeholder";
import { formatDateForLocale } from "../i18n/format";
import { t } from "../i18n";
import { showToast } from "../components/toast/toast";
import {
  shareMemorialToWhatsApp,
  shareMemorialToFacebook,
  shareMemorialToInstagram,
  shareMemorialToX,
  shareMemorialToEmail,
  shareMemorialCopyLink,
} from "./share-memorial";

declare const lucide: any;

const SESSION_KEY = "pendingCheckoutPreview";

export type CheckoutPreview = {
  pendingId: string;
  holeId: string;
  name: string;
  epitaph?: string;
  birthDate?: string;
  deathDate?: string;
  seed: string;
};

let grazieViewMounted = false;
let currentPreview: CheckoutPreview | null = null;
let currentHoleId: string | null = null;
let resolvedLegacy: LegacyHeadstone | null = null;
let hadHeadstonesBeforePayment = false;
let thanksMode: "memorial" | "pethernity" = "memorial";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function previewDateLabel(birth?: string, death?: string): string {
  if (birth && death) {
    return t("tombstone.dateRange", {
      birth: formatDateForLocale(birth),
      death: formatDateForLocale(death),
    });
  }
  if (birth) return t("tombstone.bornOn", { date: formatDateForLocale(birth) });
  if (death) return t("tombstone.passedOn", { date: formatDateForLocale(death) });
  return "";
}

export function saveCheckoutPreview(preview: CheckoutPreview): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(preview));
  } catch {
    /* ignore quota errors */
  }
}

function loadCheckoutPreview(
  pendingId?: string | null,
  holeId?: string | null,
): CheckoutPreview | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as CheckoutPreview;
      if (parsed?.name) return parsed;
    }
  } catch {
    /* ignore */
  }
  if (pendingId && holeId) {
    return {
      pendingId,
      holeId,
      name: t("view.namePlaceholder"),
      seed: pendingId,
    };
  }
  return null;
}

function clearCheckoutPreview(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function memorialCardHtml(preview: CheckoutPreview, legacy: LegacyHeadstone | null): string {
  const name = legacy?.name ?? preview.name;
  const epitaph = legacy?.epitaph ?? preview.epitaph ?? "";
  const dateLabel =
    legacy?.fullDateLabel ||
    legacy?.dateLabel ||
    previewDateLabel(preview.birthDate, preview.deathDate);
  const photoUrl = legacy?.photo ?? null;
  const seed = legacy?.id ?? preview.seed ?? preview.name;

  const avatar = renderPetPhotoHtml({
    variant: "avatar",
    photoUrl,
    seed,
    name,
    className: "grazie-card__avatar",
    alt: name,
  });

  const epitaphHtml = epitaph
    ? `<div class="grazie-card__epitaph">“${escapeHtml(epitaph)}”</div>`
    : "";

  return `
    ${avatar}
    <div class="grazie-card__info">
      <div class="grazie-card__name">${escapeHtml(name)}</div>
      ${dateLabel ? `<div class="grazie-card__dates">${escapeHtml(dateLabel)}</div>` : ""}
      ${epitaphHtml}
    </div>
  `;
}

function shareGridHtml(): string {
  const networks = [
    { id: "wa", label: t("grazie.whatsapp"), className: "grazie-share--wa", onclick: "shareThanksWhatsApp()" },
    { id: "fb", label: t("grazie.facebook"), className: "grazie-share--fb", onclick: "shareThanksFacebook()" },
    { id: "ig", label: t("grazie.instagram"), className: "grazie-share--ig", onclick: "shareThanksInstagram()" },
    { id: "x", label: t("grazie.x"), className: "grazie-share--x", onclick: "shareThanksX()" },
    { id: "mail", label: t("grazie.email"), className: "grazie-share--mail", onclick: "shareThanksEmail()" },
    { id: "copy", label: t("grazie.copyLink"), className: "grazie-share--copy", onclick: "shareThanksCopyLink()" },
  ];

  return networks
    .map(
      (n) => `
    <button type="button" class="grazie-share ${n.className}" onclick="${n.onclick}">
      <span class="grazie-share__icon" aria-hidden="true"></span>
      <span class="grazie-share__label">${escapeHtml(n.label)}</span>
    </button>
  `,
    )
    .join("");
}

function ensureGrazieView(): void {
  const host = getViewContainer("grazie");
  if (!host || grazieViewMounted) return;

  host.innerHTML = /* html */ `
    <div class="grazie-inner pscroll">
      <div class="grazie-wrap">
        <div class="grazie-hero">
          <div class="grazie-hero__deco grazie-hero__deco--left" aria-hidden="true"></div>
          <div class="grazie-hero__deco grazie-hero__deco--right" aria-hidden="true"></div>
          <div class="grazie-hero__check" aria-hidden="true">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
          </div>
          <h1 class="grazie-hero__title" id="grazie-title">${escapeHtml(t("grazie.title"))}</h1>
          <p class="grazie-hero__subtitle" id="grazie-subtitle"></p>
        </div>

        <div class="grazie-card" id="grazie-card"></div>

        <p class="grazie-share-label">${escapeHtml(t("grazie.shareLabel"))}</p>
        <div class="grazie-share-grid" id="grazie-share-grid">${shareGridHtml()}</div>

        <div class="grazie-actions">
          <button type="button" class="grazie-btn grazie-btn--primary" onclick="goToThanksProfile()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"></circle><path d="M4 20c0-4 3.6-6 8-6s8 2 8 6"></path></svg>
            ${escapeHtml(t("grazie.goProfile"))}
          </button>
          <button type="button" class="grazie-btn grazie-btn--secondary" onclick="goToThanksGarden()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            ${escapeHtml(t("grazie.goGarden"))}
          </button>
        </div>
      </div>
    </div>
  `;

  grazieViewMounted = true;
}

function renderGrazieContent(): void {
  if (!currentPreview) return;

  const subtitle = document.getElementById("grazie-subtitle");
  const card = document.getElementById("grazie-card");
  const title = document.getElementById("grazie-title");
  const shareGrid = document.getElementById("grazie-share-grid");

  if (title) {
    title.textContent =
      thanksMode === "pethernity" ? t("pethernity.thanksTitle") : t("grazie.title");
  }
  if (subtitle) {
    subtitle.innerHTML =
      thanksMode === "pethernity"
        ? t("pethernity.thanksSubtitle")
        : t("grazie.subtitle", {
            name: `<strong>${escapeHtml(currentPreview.name)}</strong>`,
          });
  }
  if (card) {
    card.hidden = thanksMode === "pethernity";
    if (thanksMode !== "pethernity") {
      card.innerHTML = memorialCardHtml(currentPreview, resolvedLegacy);
    }
  }
  const shareLabel = document.querySelector(".grazie-share-label");
  if (shareLabel) (shareLabel as HTMLElement).hidden = thanksMode === "pethernity";
  if (shareGrid) {
    shareGrid.hidden = thanksMode === "pethernity";
    shareGrid.innerHTML = shareGridHtml();
    shareGrid.classList.toggle("is-pending", !resolvedLegacy);
  }

  if (typeof lucide !== "undefined") lucide.createIcons();
}

function findHeadstoneByHole(holeId: string): HeadstoneDTO | null {
  return (
    getMyHeadstones().find((h) => holeIdFromCoords(h.x, h.y) === holeId) ?? null
  );
}

async function pollForHeadstone(holeId: string): Promise<LegacyHeadstone | null> {
  for (let attempt = 0; attempt < 10; attempt++) {
    await refreshPostsAccess();
    const match = findHeadstoneByHole(holeId);
    if (match) return dtoToLegacy(match);
    if (attempt < 9) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  }
  return null;
}

function maybeShowDiscoverMemoriesToast(): void {
  if (hadHeadstonesBeforePayment || !getHasHeadstones()) return;
  setTimeout(() => {
    showToast(t("payment.discoverMemories"), "info", 5000);
  }, 1500);
}

function getThanksShareTarget(): LegacyHeadstone | null {
  return resolvedLegacy;
}

function guardShare(action: (legacy: LegacyHeadstone) => void | Promise<void>): void {
  const legacy = getThanksShareTarget();
  if (!legacy) {
    showToast(t("grazie.sharePending"), "info", 6000);
    return;
  }
  Promise.resolve(action(legacy)).catch(() => null);
}

export function shareThanksWhatsApp(): void {
  guardShare((legacy) => shareMemorialToWhatsApp(legacy));
}

export function shareThanksFacebook(): void {
  guardShare((legacy) => shareMemorialToFacebook(legacy));
}

export function shareThanksInstagram(): void {
  guardShare((legacy) => shareMemorialToInstagram(legacy));
}

export function shareThanksX(): void {
  guardShare((legacy) => shareMemorialToX(legacy));
}

export function shareThanksEmail(): void {
  guardShare((legacy) => shareMemorialToEmail(legacy));
}

export function shareThanksCopyLink(): void {
  guardShare((legacy) => shareMemorialCopyLink(legacy));
}

export function goToThanksProfile(): void {
  openProfile().catch(() => null);
}

export function goToThanksGarden(): void {
  showView("garden");
  if (resolvedLegacy) {
    navigateToHeadstone(resolvedLegacy);
    return;
  }
  if (currentHoleId) {
    navigateToMemorial({ holeId: currentHoleId, openModal: true });
  }
}

export async function openMemorialThanks(opts: {
  headstone?: HeadstoneDTO | null;
  holeId?: string | null;
  name?: string;
  epitaph?: string;
  birthDate?: string;
  deathDate?: string;
}): Promise<void> {
  thanksMode = "memorial";
  const holeId = opts.holeId || "";
  currentPreview = {
    pendingId: opts.headstone?.id || "created",
    holeId,
    name: opts.name || opts.headstone?.pet?.name || t("view.namePlaceholder"),
    epitaph: opts.epitaph,
    birthDate: opts.birthDate,
    deathDate: opts.deathDate,
    seed: opts.headstone?.id || opts.name || "created",
  };
  currentHoleId = holeId;
  resolvedLegacy = opts.headstone ? dtoToLegacy(opts.headstone) : null;
  hadHeadstonesBeforePayment = false;

  ensureGrazieView();
  renderGrazieContent();
  showView("grazie");
  maybeShowDiscoverMemoriesToast();
}

export async function openPethernityThanks(): Promise<void> {
  thanksMode = "pethernity";
  currentPreview = {
    pendingId: "pethernity",
    holeId: "",
    name: "Pethernity",
    seed: "pethernity",
  };
  currentHoleId = null;
  resolvedLegacy = null;
  ensureGrazieView();
  renderGrazieContent();
  showView("grazie");
}

export async function openPaymentThanks(opts: {
  holeId?: string | null;
  pendingId?: string | null;
  hadHeadstonesBefore?: boolean;
}): Promise<void> {
  thanksMode = "memorial";
  const preview = loadCheckoutPreview(opts.pendingId, opts.holeId);
  if (!preview) return;

  currentPreview = preview;
  currentHoleId = preview.holeId;
  resolvedLegacy = null;
  hadHeadstonesBeforePayment = !!opts.hadHeadstonesBefore;

  ensureGrazieView();
  renderGrazieContent();
  showView("grazie");

  const legacy = await pollForHeadstone(preview.holeId);
  if (legacy && currentPreview?.holeId === preview.holeId) {
    resolvedLegacy = legacy;
    renderGrazieContent();
    maybeShowDiscoverMemoriesToast();
  }

  clearCheckoutPreview();
}

export function refreshGrazieOnLocale(): void {
  if (getCurrentView() !== "grazie" || !currentPreview) return;
  grazieViewMounted = false;
  const host = getViewContainer("grazie");
  if (host) host.innerHTML = "";
  ensureGrazieView();
  renderGrazieContent();
}
